import React, { useMemo } from 'react'
import styled from 'styled-components'
import { Button, message as antdMessage } from 'antd'
import WizrInput from '../WizrInput'
import WizrText from '../WizrText'
import WizrButton from '../WizrButton'
import Copy from '../../assets/icons/Copy'
import copy from 'copy-to-clipboard'
import CodeEditor from '@uiw/react-textarea-code-editor'

type InsightsCustomApiModalProps = {
  endpoint: string
  payloadText: string
  onNavigateToAccounts: () => void
}

const PayloadCodeShell = styled.div`
  position: relative;
  height: 230px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #333;
  background: #212124;
  font: 13px/21px Monaco, Menlo, 'Ubuntu Mono', monospace;
`

const PayloadScrollRow = styled.div`
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  align-items: stretch;
  min-height: 100%;
  height: 100%;
  overflow: auto;
`

const PayloadLineNumbers = styled.div`
  position: relative;
  z-index: 1;
  background: #000;
  padding: 12px 4px 48px;
  border-right: 1px solid #1a1a1a;
  color: #4d4d4d;
  user-select: none;
  min-width: 22px;
  text-align: center;

  .line-number {
    height: 21px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const PayloadCopy = styled.div`
  position: absolute;
  right: 8px;
  bottom: 8px;
  z-index: 2;
`

const PayloadReadonlyEditor = styled(CodeEditor)`
  min-width: 0;
  width: 100%;
  border: none !important;
  font: inherit !important;
  line-height: 21px !important;
  background-color: #212124 !important;
  --color-fg-default: #fff;
  color: #fff !important;

  .w-tc-editor-preview,
  .w-tc-editor-text {
    background-color: #212124 !important;
    white-space: pre !important;
    padding: 12px 48px 48px 12px !important;
  }

  .w-tc-editor-preview pre code {
    color: inherit !important;
  }

  .w-tc-editor-text {
    caret-color: transparent;
    opacity: 1 !important;
    background: transparent !important;
    -webkit-text-fill-color: transparent !important;
  }
`

function InsightsCustomApiModal({
  endpoint,
  payloadText,
  onNavigateToAccounts,
}: InsightsCustomApiModalProps) {
  const { lineNumbers, editorMinHeight } = useMemo(() => {
    const lineCount = Math.max(payloadText.split('\n').length, 1)
    return {
      lineNumbers: Array.from({ length: lineCount }, (_, i) => i + 1),
      // @uiw preview adds one trailing visual line after the code block.
      editorMinHeight: (lineCount + 1) * 21 + 48,
    }
  }, [payloadText])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, flex: 1, minHeight: 0, width: '100%', textAlign: 'left' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', textAlign: 'left' }}>
        <WizrText type="body2" textColor="#707276" style={{ textAlign: 'left' }}>API Endpoint</WizrText>
        <WizrInput
          readOnly
          value={endpoint}
          background="#F2F4F9"
          preComponent={(
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '0 10px',
                marginRight: 4,
                background: '#E5E7EB',
                borderRadius: 6,
                fontWeight: 700,
                fontSize: 12,
                color: '#1D1F22',
                height: 28,
                fontFamily: 'Montserrat, sans-serif',
              }}
            >
              POST
            </span>
          )}
          suffix={(
            <WizrButton
              type="link"
              icon={<Copy />}
              onClick={() => {
                copy(endpoint)
                antdMessage.success('Endpoint copied to clipboard', 2)
              }}
            />
          )}
        />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left' }}>
        <WizrText type="body2" textColor="#707276" style={{ textAlign: 'left' }}>Wizr-Access-Key:</WizrText>
        <span
          role="link"
          tabIndex={0}
          style={{ cursor: 'pointer', display: 'inline-block' }}
          onClick={onNavigateToAccounts}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onNavigateToAccounts()
            }
          }}
        >
          <WizrText
            type="body2"
            textColor="#3525B9"
            style={{ wordBreak: 'break-all' }}
          >
            API_KEY
          </WizrText>
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%', textAlign: 'left' }}>
        <WizrText type="body2" textColor="#707276" style={{ textAlign: 'left' }}>Payload</WizrText>
        <PayloadCodeShell>
          <PayloadScrollRow>
            <PayloadLineNumbers>
              {lineNumbers.map((lineNumber) => (
                <div key={lineNumber} className="line-number">
                  {lineNumber}
                </div>
              ))}
            </PayloadLineNumbers>
            <PayloadReadonlyEditor
              value={payloadText}
              readOnly
              padding={0}
              minHeight={editorMinHeight}
              rehypePlugins={[]}
            />
          </PayloadScrollRow>
          <PayloadCopy>
            <Button
              type="text"
              icon={<Copy color="#FFFFFF" />}
              onClick={() => {
                copy(payloadText)
                antdMessage.success('Payload copied to clipboard', 2)
              }}
              aria-label="Copy payload"
            />
          </PayloadCopy>
        </PayloadCodeShell>
      </div>
    </div>
  )
}

export default InsightsCustomApiModal
